import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Bell, LogOut, MapPin, CheckCircle2, Circle, Loader2, Navigation, Calendar, Clock, Camera, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Modal from "@/components/Modal";
import LeadTeamPanel from "@/components/LeadTeamPanel";

const PRIORITY = {
  high: { label: "High", cls: "bg-[#FF6B6B]/15 text-[#c0392b] border-[#FF6B6B]/40" },
  medium: { label: "Medium", cls: "bg-[#FFC43D]/20 text-[#946200] border-[#FFC43D]/50" },
  low: { label: "Low", cls: "bg-[#2EC4B6]/15 text-[#0d7d72] border-[#2EC4B6]/40" },
};

export default function CrewDashboard() {
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [checkOpen, setCheckOpen] = useState(false);
  const [checkTask, setCheckTask] = useState("");
  const [note, setNote] = useState("");
  const [locating, setLocating] = useState(false);
  const [view, setView] = useState("work");
  const [photo, setPhoto] = useState(null);
  const photoRef = useRef(null);

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const img = new window.Image();
    const reader = new FileReader();
    reader.onload = () => { img.src = reader.result; };
    img.onload = () => {
      const scale = Math.min(1, 1280 / img.width);
      const c = document.createElement("canvas");
      c.width = Math.round(img.width * scale);
      c.height = Math.round(img.height * scale);
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      setPhoto(c.toDataURL("image/jpeg", 0.8));
    };
    reader.readAsDataURL(file);
  };

  const load = useCallback(async () => {
    try { const r = await api.get("/crew/workspace"); setData(r.data); } catch (e) {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleComplete = async (t) => {
    const done = t.status !== "done";
    setData((d) => ({ ...d, tasks: d.tasks.map((x) => (x.id === t.id ? { ...x, status: done ? "done" : "todo" } : x)) }));
    try { await api.patch(`/crew/tasks/${t.id}/complete`, { done }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); load(); }
  };

  const openCheckIn = (taskId = "") => { setCheckTask(taskId); setNote(""); setPhoto(null); setCheckOpen(true); };

  const submitCheckIn = () => {
    if (!navigator.geolocation) return toast.error("Location is not available on this device");
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await api.post("/crew/checkin", {
            lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy,
            note: note.trim(), task_id: checkTask || null, photo: photo || null,
          });
          toast.success("Checked in — your location and time were sent to the owner");
          setCheckOpen(false);
          load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setLocating(false); }
      },
      (e) => { setLocating(false); toast.error(e.code === 1 ? "Location permission denied — enable it to check in" : "Could not get your location"); },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
    );
  };

  const tasks = data?.tasks || [];
  const open = tasks.filter((t) => t.status !== "done");
  const done = tasks.filter((t) => t.status === "done");

  return (
    <div className="min-h-screen app-canvas" data-testid="crew-dashboard">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#F9F9F7]/85 border-b border-[var(--line)]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[var(--accent)] flex items-center justify-center"><Bell className="w-5 h-5 text-white" strokeWidth={2.4} /></div>
            <div className="leading-tight">
              <p className="font-mono text-xs tracking-[0.18em] uppercase">{data?.business || "Crew"}</p>
              <p className="text-xs text-[var(--text-2)]">Crew workspace</p>
            </div>
          </div>
          <button data-testid="crew-logout" onClick={logout} className="flex items-center gap-2 h-9 px-3 border border-[var(--line)] bg-white text-sm hover:border-[var(--accent)] transition-colors"><LogOut className="w-4 h-4" /> Sign out</button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        <div className="flex items-end justify-between gap-4">
          <div>
            <p className="label-mono text-[var(--text-2)]">Hi {data?.name || user?.name}</p>
            <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-2">Your tasks</h1>
          </div>
          <button data-testid="crew-checkin-btn" onClick={() => openCheckIn("")} className="flex items-center gap-2 h-12 px-5 bg-[#1A1A18] text-white hover:bg-[var(--accent)] transition-colors shrink-0">
            <MapPin className="w-4 h-4" /> Check in on site
          </button>
        </div>

        {user?.team_lead && (
          <div className="inline-flex border border-[var(--line)] w-max" data-testid="crew-view-toggle">
            <button data-testid="crew-view-work" onClick={() => setView("work")} className={`h-9 px-4 text-sm transition-colors ${view === "work" ? "bg-[#1A1A18] text-white" : "text-[var(--text-2)] hover:text-[var(--accent)]"}`}>My work</button>
            <button data-testid="crew-view-team" onClick={() => setView("team")} className={`h-9 px-4 text-sm transition-colors ${view === "team" ? "bg-[#1A1A18] text-white" : "text-[var(--text-2)] hover:text-[var(--accent)]"}`}>Team dashboard</button>
          </div>
        )}

        {view === "team" ? (
          <LeadTeamPanel />
        ) : !data ? (
          <div className="grid place-items-center py-16"><Loader2 className="w-7 h-7 animate-spin text-[var(--accent)]" /></div>
        ) : (
          <>
            <section className="space-y-3">
              {open.length === 0 && <div className="border border-[var(--line)] bg-white p-10 text-center text-sm text-[var(--text-2)]">No open tasks assigned to you right now.</div>}
              {open.map((t) => {
                const p = PRIORITY[t.priority] || PRIORITY.medium;
                return (
                  <div key={t.id} data-testid={`crew-task-${t.id}`} className="border border-[var(--line)] bg-white p-4 flex items-start gap-3">
                    <button data-testid={`crew-complete-${t.id}`} onClick={() => toggleComplete(t)} className="mt-0.5 text-[var(--text-2)] hover:text-[var(--accent)] transition-colors"><Circle className="w-6 h-6" /></button>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${p.cls}`}>{p.label}</span>
                        {t.due_date && <span className="text-xs text-[var(--text-2)] flex items-center gap-1"><Calendar className="w-3 h-3" /> {t.due_date}</span>}
                      </div>
                      <p className="text-sm font-medium mt-2">{t.title}</p>
                    </div>
                    <button data-testid={`crew-task-checkin-${t.id}`} onClick={() => openCheckIn(t.id)} className="shrink-0 inline-flex items-center gap-1.5 h-9 px-3 border border-[var(--line)] text-xs hover:border-[var(--accent)] transition-colors"><Navigation className="w-3.5 h-3.5" /> Check in</button>
                  </div>
                );
              })}
            </section>

            {done.length > 0 && (
              <section>
                <p className="label-mono text-[var(--text-2)] mb-3">Completed</p>
                <div className="space-y-2">
                  {done.map((t) => (
                    <div key={t.id} data-testid={`crew-task-${t.id}`} className="border border-[var(--line)] bg-[#f4f4f2] p-3 flex items-center gap-3">
                      <button data-testid={`crew-complete-${t.id}`} onClick={() => toggleComplete(t)} className="text-[#2EC4B6]"><CheckCircle2 className="w-6 h-6" /></button>
                      <p className="text-sm line-through text-[var(--text-2)] flex-1">{t.title}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <section>
              <p className="label-mono text-[var(--text-2)] mb-3">Recent check-ins</p>
              {(data.checkins || []).length === 0 ? (
                <p className="text-sm text-[var(--text-2)]">No check-ins yet. Tap “Check in on site” when you arrive.</p>
              ) : (
                <div className="space-y-2">
                  {data.checkins.map((c) => (
                    <div key={c.id} data-testid={`crew-checkin-${c.id}`} className="border border-[var(--line)] bg-white p-3 flex items-center gap-3 text-sm">
                      <MapPin className="w-4 h-4 text-[var(--accent)] shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate">{c.task_title || "On-site check-in"}{c.note ? ` — ${c.note}` : ""}</p>
                        <p className="text-xs text-[var(--text-2)] font-mono flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(c.created_at).toLocaleString()}</p>
                      </div>
                      {c.maps_url && <a href={c.maps_url} target="_blank" rel="noreferrer" className="text-xs text-[var(--accent)] hover:underline shrink-0">Map</a>}
                      {c.photo_url && <a data-testid={`checkin-photo-${c.id}`} href={`${process.env.REACT_APP_BACKEND_URL}${c.photo_url}`} target="_blank" rel="noreferrer" className="shrink-0"><img src={`${process.env.REACT_APP_BACKEND_URL}${c.photo_url}`} alt="proof" className="w-9 h-9 object-cover rounded border border-[var(--line)]" /></a>}
                    </div>
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </main>

      <Modal open={checkOpen} onClose={() => setCheckOpen(false)} testid="crew-checkin-modal" subtitle="Site check-in" title="Log a check-in">
        <div className="space-y-4">
          <p className="text-sm text-[var(--text-2)]">We'll capture your current GPS location and the time, and share it with the owner.</p>
          <div>
            <span className="label-mono text-[var(--text-2)]">Task (optional)</span>
            <select data-testid="checkin-task" value={checkTask} onChange={(e) => setCheckTask(e.target.value)} className="mt-2 w-full h-11 px-3 border border-[var(--line)] bg-white outline-none focus:border-[var(--accent)]">
              <option value="">General on-site check-in</option>
              {open.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
            </select>
          </div>
          <div>
            <span className="label-mono text-[var(--text-2)]">Note (optional)</span>
            <textarea data-testid="checkin-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Arrived on site…" className="mt-2 w-full px-3 py-2 border border-[var(--line)] bg-white outline-none focus:border-[var(--accent)]" />
          </div>
          <div>
            <span className="label-mono text-[var(--text-2)]">Job photo (optional)</span>
            {photo ? (
              <div className="mt-2 relative w-full">
                <img data-testid="checkin-photo-preview" src={photo} alt="Job proof" className="w-full max-h-52 object-cover rounded border border-[var(--line)]" />
                <button data-testid="checkin-photo-remove" onClick={() => { setPhoto(null); if (photoRef.current) photoRef.current.value = ""; }} className="absolute top-2 right-2 w-7 h-7 grid place-items-center rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors"><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <button data-testid="checkin-photo-btn" onClick={() => photoRef.current?.click()} className="mt-2 w-full h-11 flex items-center justify-center gap-2 border border-dashed border-[var(--text-2)] text-sm hover:border-[var(--accent)] transition-colors"><Camera className="w-4 h-4" /> Add a job photo</button>
            )}
            <input ref={photoRef} data-testid="checkin-photo-input" type="file" accept="image/*" capture="environment" onChange={onPhoto} className="hidden" />
          </div>
          <button data-testid="checkin-submit" onClick={submitCheckIn} disabled={locating} className="w-full h-11 flex items-center justify-center gap-2 bg-[var(--accent)] text-white hover:opacity-90 transition-opacity disabled:opacity-50">
            {locating ? <><Loader2 className="w-4 h-4 animate-spin" /> Getting location…</> : <><MapPin className="w-4 h-4" /> Capture location & check in</>}
          </button>
        </div>
      </Modal>
    </div>
  );
}
